import { CountriesFlagsForLanguage } from '@/components/countries';
import { ProfilePictureWooDark } from '@/components/profile-picture/profile-picture.woo-dark';
import { useGetPersonalInfo } from '@/hooks/api';
import { auth, formatDate, normalizeName } from '@/lib';
import { WooSignOut, WooChevronRight } from '@nosinovacao/fe-icons';
import { ActionCard, Button, useI18n } from '@nosinovacao/nosid-mfe-common';
import { type ReactNode } from 'react';
import { type SettingsStep, type StepNavigation } from './models';

export function SettingsMainSectionWooDark({
  goToStep,
}: Readonly<StepNavigation<SettingsStep, never>>) {
  const { data } = useGetPersonalInfo();
  const { t, language } = useI18n();
  const CountryFlag = CountriesFlagsForLanguage[language] || <></>;
  return (
    <>
      <div className="mt-8 flex items-center gap-4 pb-1 pt-5 tablet:mt-3">
        <ProfilePictureWooDark
          className="shrink-0"
          size={72}
          name={data?.DisplayName}
          src={data?.PamPictureUri}
        />
        <div className="grid gap-1 text-nos-neutral-0 font-title-h4">
          <span className="truncate font-bold">{normalizeName(data?.DisplayName ?? '')}</span>
          {data?.BirthDate ? (
            <span className="font-light text-nos-neutral-300">{formatDate(data?.BirthDate)}</span>
          ) : null}
        </div>
      </div>
      <div className="-mx-5 flex h-full flex-col overflow-y-auto bg-nos-neutral-950 tablet:-mx-8">
        <div className="mb-2 h-4 shrink-0 bg-nos-neutral-950 [clip-path:_polygon(100%_0,_0%_100%,_0_0)]" />
        <div className="grid gap-7 px-5 tablet:px-8">
          <SettingsItem title={t('Common.UserSettings.Configurations')}>
            <li>
              <ActionCard
                skin="woo-dark"
                variant="quick-link"
                preTitle={t('Common.Language.CardTitle')}
                subTitle={<></>}
                title={
                  <div className="flex items-center gap-2">
                    {t(`Common.Language.Options.${language}`)} <CountryFlag />
                  </div>
                }
                icon={<WooChevronRight width={20} height={20} />}
                className="w-full border-nos-neutral-800 bg-nos-neutral-950"
                data-tid="card-language"
                onClick={() => {
                  goToStep?.('language');
                }}
              />
            </li>
          </SettingsItem>
        </div>
        <div className="mt-auto grid h-fit place-items-center gap-2 pb-8 pt-20">
          <Button
            skin="woo-dark"
            variant="tertiary"
            onClick={() => auth.logout(false)}
            data-tid="button-logout"
          >
            <WooSignOut width={20} height={20} /> {t('Common.Logout')}
          </Button>
          <span className="font-light text-nos-neutral-400 font-text-p2">
            {t('Common.UserSettings.Rights')}
          </span>
        </div>
      </div>
    </>
  );
}

function SettingsItem({ children, title }: Readonly<{ title: string; children: ReactNode }>) {
  return (
    <div className="grid gap-2">
      <span className="font-light text-nos-neutral-200 font-text-p2">{title}</span>
      <ul className="grid gap-2">{children}</ul>
    </div>
  );
}
